import React, { useEffect, useRef, useState } from "react";
import flowFrameLogo from "../data/FlowFrame Logo.png";
import flowFrameIcon from "../data/FlowFrame - large.png";
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { Plus } from 'lucide-react';
import PWAInstallPrompt from './PWAInstallPrompt';
import PWAInstallInstructions from './PWAInstallInstructions';

// Landing-page counters. The hourly `updatePlatformStats` Cloud Function
// writes systemStats/landingPageStats; we subscribe to that doc directly
// (public-read rule) so there is no HTTP cold start to race against.
const STATS_DOC = ['systemStats', 'landingPageStats'];
const STATS_CACHE_KEY = 'platformStats';
// Last-resort numbers, shown only when the doc can't be read and nothing
// real is cached. They are never written to the cache.
const FALLBACK_STATS = {
  musiciansEmpowered: 350,
  practiceHoursTracked: 6000,
  customPatternsCreated: 250,
};

const LandingPage = ({
  onNavigateToMetronome = () => {},
  onNavigateToTracker = () => {},
  onNavigateToLibrary = () => {},
  onNavigateToCommunity = () => {},
  onNavigateToAbout = () => {},
  onNavigateToScores = () => {},
  onGetStarted = () => {},
  showLoginModal = () => {},
  user = null,
  isLoggedIn = false,
}) => {
  const [isVisible, setIsVisible] = useState({});
  const observerRef = useRef();
  const statsRef = useRef();
  const [countersAnimated, setCountersAnimated] = useState(false);

  // Initialize with loading state instead of hardcoded values
  const DEFAULT_STATS = {
    musiciansEmpowered: 0,
    practiceHoursTracked: 0,
    customPatternsCreated: 0,
    loading: true,
    error: null,
    isFailback: false,
    cached: false
  };

  // Initialize with defaults instead of loading state
  const [platformStats, setPlatformStats] = useState(DEFAULT_STATS);


  // Updated useEffect for Adobe Fonts
  useEffect(() => {
    // Check if fonts are already loaded
    if (!document.querySelector('link[href*="use.typekit.net"]')) {
      // Adobe Fonts for New Zen
      const linkAdobe = document.createElement("link");
      linkAdobe.href = "https://use.typekit.net/brj0ykh.css"; // Replace with actual kit ID
      linkAdobe.rel = "stylesheet";
      document.head.appendChild(linkAdobe);

      // Google Fonts for Lato
      const linkLato = document.createElement("link");
      linkLato.href =
        "https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,100;0,300;0,400;0,700;0,900;1,100;1,300;1,400;1,700;1,900&display=swap";
      linkLato.rel = "stylesheet";
      document.head.appendChild(linkLato);
    }
  }, []);

  // Intersection Observer for animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setIsVisible((prev) => ({
            ...prev,
            [entry.target.dataset.fadeId]: true,
          }));
        }
      });
    }, observerOptions);

    // Observe all fade-in elements
    document.querySelectorAll("[data-fade-id]").forEach((el) => {
      observerRef.current.observe(el);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    let haveRealNumbers = false;

    // Paint instantly from the last real numbers this browser saw.
    try {
      const cached = JSON.parse(localStorage.getItem(STATS_CACHE_KEY) || 'null');
      if (cached?.data?.musiciansEmpowered > 0 && !cached.data.isFailback) {
        haveRealNumbers = true;
        setPlatformStats({ ...cached.data, loading: false, error: null, isFailback: false, cached: true });
      } else if (cached) {
        localStorage.removeItem(STATS_CACHE_KEY); // old fallback / poisoned entry
      }
    } catch {
      localStorage.removeItem(STATS_CACHE_KEY);
    }

    const useFallback = (reason) => {
      if (haveRealNumbers) return; // stale-but-real beats estimated
      setPlatformStats({
        ...FALLBACK_STATS,
        lastUpdated: null,
        loading: false,
        error: reason,
        isFailback: true,
        cached: false,
      });
    };

    // Live subscription: updates in place when the hourly job runs.
    const unsubscribe = onSnapshot(
      doc(db, ...STATS_DOC),
      (snap) => {
        if (!snap.exists()) {
          useFallback('Statistics have not been computed yet');
          return;
        }
        const d = snap.data() || {};
        const next = {
          musiciansEmpowered: Number(d.musiciansEmpowered) || 0,
          practiceHoursTracked: Number(d.practiceHoursTracked) || 0,
          customPatternsCreated: Number(d.customPatternsCreated) || 0,
          lastUpdated: d.lastUpdated || null,
          loading: false,
          error: null,
          isFailback: false,
          cached: false,
        };
        haveRealNumbers = next.musiciansEmpowered > 0;
        setPlatformStats(next);
        setCountersAnimated(false);
        try {
          localStorage.setItem(STATS_CACHE_KEY, JSON.stringify({ data: next, timestamp: Date.now() }));
        } catch {
          /* storage unavailable — fine, we still have the live values */
        }
      },
      (err) => {
        console.warn('[landing] platform stats read failed:', err?.code || err?.message || err);
        useFallback(err?.code === 'permission-denied' ? 'Statistics unavailable' : 'Could not load statistics');
      }
    );

    return unsubscribe;
  }, []);

  // Update the animateCounter function to handle the dynamic data
  const animateCounter = (element, target) => {
    const duration = 2000;
    const increment = target / (duration / 16);
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        current = target;
        clearInterval(timer);
      }
      element.textContent = Math.floor(current).toLocaleString();
    }, 16);
  };

  // Update Stats counter observer to use real data
  useEffect(() => {
    const statsObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !countersAnimated && !platformStats.loading) {
            setCountersAnimated(true);
            const counters = entry.target.querySelectorAll("[data-counter]");
            counters.forEach((counter, index) => {
              let target;
              switch(index) {
                case 0:
                  target = platformStats.musiciansEmpowered;
                  break;
                case 1:
                  target = platformStats.practiceHoursTracked;
                  break;
                case 2:
                  target = platformStats.customPatternsCreated;
                  break;
                default:
                  target = parseInt(counter.dataset.counter);
              }
              animateCounter(counter, target);
            });
          }
        });
      },
      { threshold: 0.5 }
    );

    if (statsRef.current) {
      statsObserver.observe(statsRef.current);
    }

    return () => statsObserver.disconnect();
  }, [countersAnimated, platformStats.loading]);

  // Add this useEffect to force counter updates when stats change
  useEffect(() => {
    // Force counter update when stats change and are not loading
    if (!platformStats.loading && platformStats.musiciansEmpowered > 0) {
      const statsSection = statsRef.current;
      if (statsSection) {
        const counters = statsSection.querySelectorAll("[data-counter]");
        counters.forEach((counter, index) => {
          let target;
          switch(index) {
            case 0:
              target = platformStats.musiciansEmpowered;
              break;
            case 1:
              target = platformStats.practiceHoursTracked;
              break;
            case 2:
              target = platformStats.customPatternsCreated;
              break;
            default:
              target = parseInt(counter.dataset.counter) || 0;
          }
          
          // Update counter with smooth animation
          animateCounter(counter, target);
        });
      }
    }
  }, [platformStats.musiciansEmpowered, platformStats.practiceHoursTracked, platformStats.customPatternsCreated, platformStats.loading]);

  const smoothScroll = (targetId) => {
    const target = document.getElementById(targetId);
    if (target) {
      target.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // Function to get user's display name
  const getUserDisplayName = () => {
    if (!user) return "Musician";
    return user.displayName || user.email?.split('@')[0] || "Musician";
  };
  

  const PWAPromptButton = ({ className = "" }) => {
    const [isIOS, setIsIOS] = useState(false);
    const [isInPWA, setIsInPWA] = useState(false);

    useEffect(() => {
      const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const inPWA = window.matchMedia('(display-mode: standalone)').matches || 
                    window.navigator.standalone === true;
      setIsIOS(iOS);
      setIsInPWA(inPWA);
    }, []);

    if (!isIOS || isInPWA) return null;

    return (
      <button
        onClick={() => setShowPWAInstructions(true)}
        className={`inline-flex items-center space-x-2 bg-gradient-to-r from-accent-600 to-brand-500 text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity ${className}`}
      >
        <Plus className="w-4 h-4" />
        <span>Install App</span>
      </button>
    );
  };

  return (
    <div className="landing-page">
      <style jsx>{`
        .landing-page * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .landing-page {
          font-family: "Inter", "Lato", -apple-system, BlinkMacSystemFont, "Segoe UI",
            Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
          line-height: 1.6;
          color: var(--color-text-primary);
          overflow-x: hidden;
        }

        .user-welcome {
          position: absolute;
          top: 2rem;
          right: 2rem;
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px);
          padding: 0.75rem 1.5rem;
          border-radius: 25px;
          color: var(--color-primary-700);
          font-family: "Inter", "Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          font-size: 0.9rem;
          font-weight: 500;
          z-index: 10;
          animation: slideInDown 1s ease-out 0.8s both;
        }

        @keyframes slideInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .cta-button {
          background: linear-gradient(135deg, var(--color-primary-700), var(--color-primary-500));
          color: white;
          padding: 1rem 2rem;
          border: none;
          border-radius: 50px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: "Inter", "Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          font-size: 1.1rem;
          min-height: 48px;
        }

        .cta-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(52, 108, 191, 0.3);
        }

        .cta-button:active {
          transform: translateY(0);
        }

        .cta-button.pulse {
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(52, 108, 191, 0.7);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(52, 108, 191, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(52, 108, 191, 0);
          }
        }

        .hero {
          min-height: 80vh;
          background: linear-gradient(135deg, var(--color-surface-sunken) 0%, #c3cfe2 100%);
          display: flex;
          align-items: flex-start;
          position: relative;
          overflow: hidden;
          padding: 4rem 0 2rem;
        }

        .hero::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 100px;
          background: linear-gradient(to bottom, transparent 0%, #fafbfc 70%, #ffffff 100%);
          z-index: 1;
        }

        .hero-content {
          z-index: 2;
          position: relative;
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
          text-align: center;
        }

        .hero .tagline {
          font-family: "Inter", "Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          font-size: 1.5rem;
          font-weight: 300;
          color: #666;
          margin-bottom: 2rem;
          animation: slideInUp 1s ease-out 0.2s both;
          font-style: italic;
        }

        .hero .description {
          font-family: "Inter", "Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          font-size: 1.1rem;
          font-weight: 400;
          color: #777;
          margin-bottom: 3rem;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
          animation: slideInUp 1s ease-out 0.4s both;
          line-height: 1.7;
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .hero-buttons {
          display: flex;
          gap: 1rem;
          justify-content: center;
          animation: slideInUp 1s ease-out 0.6s both;
        }

        .secondary-button {
          background: transparent;
          color: var(--color-primary-700);
          border: 2px solid var(--color-primary-700);
          padding: 1rem 2rem;
          border-radius: 50px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: "Inter", "Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          min-height: 48px;
        }

        .secondary-button:hover {
          background: var(--color-primary-700);
          color: white;
          transform: translateY(-2px);
        }

        .secondary-button:active {
          transform: translateY(0);
        }

        .features {
          padding: 5rem 2rem;
          background: #fafbfc;
          margin-top: -50px; /* Overlap to blend */
          position: relative;
          z-index: 3;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .section-title {
          text-align: center;
          font-family: "Inter", "Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
          color: var(--color-text-primary);
        }

        .section-subtitle {
          text-align: center;
          font-family: "Inter", "Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          font-size: 1.1rem;
          font-weight: 400;
          color: #666;
          margin-bottom: 4rem;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
          line-height: 1.6;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          margin-top: 3rem;
        }

        .feature-card {
          background: white;
          padding: 2.5rem;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
          border: 1px solid #f0f0f0;
          cursor: pointer;
          min-height: 48px;
        }

        .feature-card:hover {
          transform: translateY(-10px) scale(1.02);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
        }

        .feature-card:active {
          transform: translateY(-5px) scale(1.01);
        }

        .feature-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, var(--color-primary-700), var(--color-primary-500));
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.5rem;
          font-size: 1.5rem;
          color: white;
        }

        .feature-card h3 {
          font-family: "Inter", "Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 1rem;
          color: var(--color-text-primary);
        }

        .feature-card p {
          font-family: "Inter", "Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          font-weight: 400;
          color: #666;
          line-height: 1.7;
          margin-bottom: 1.5rem;
        }

        .feature-link {
          font-family: "Inter", "Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          color: var(--color-primary-700);
          font-weight: 600;
          transition: color 0.3s ease;
        }

        .feature-link:hover {
          color: var(--color-primary-500);
        }

        .stats {
          background: linear-gradient(135deg, var(--color-primary-700), var(--color-primary-500));
          color: white;
          padding: 4rem 2rem;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 2rem;
          text-align: center;
        }

        .stat-item h3 {
          font-family: "Inter", "Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
        }

        .stat-item p {
          font-family: "Inter", "Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          font-size: 1.1rem;
          font-weight: 400;
          opacity: 0.9;
        }

        .cta-section {
          background: #f8fafc;
          padding: 5rem 2rem;
          text-align: center;
        }

        .cta-content {
          max-width: 600px;
          margin: 0 auto;
        }

        .cta-section h2 {
          font-family: "Inter", "Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
          color: var(--color-text-primary);
        }

        .cta-section p {
          font-family: "Inter", "Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          font-size: 1.1rem;
          font-weight: 400;
          color: #666;
          margin-bottom: 2rem;
          line-height: 1.6;
        }

        .fade-in {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.6s ease;
        }

        .fade-in.visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .user-welcome {
            top: 1rem;
            right: 1rem;
            font-size: 0.8rem;
            padding: 0.5rem 1rem;
          }

          .hero {
            padding: 2rem 1rem 2rem;
          }

          .hero .tagline {
            font-size: 1.2rem;
          }

          .hero .description {
            font-size: 1rem;
          }

          .hero-buttons {
            flex-direction: column;
            align-items: center;
            gap: 1rem;
            width: 100%;
            max-width: 320px;
            margin: 0 auto;
          }

          .hero-buttons .cta-button,
          .hero-buttons .secondary-button {
            width: 100%;
            text-align: center;
          }

          .features {
            padding: 3rem 1rem;
          }

          .features-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }

          .feature-card {
            padding: 2rem;
          }

          .section-title {
            font-size: 2rem;
          }

          .section-subtitle {
            font-size: 1rem;
            padding: 0 1rem;
          }

          .stats {
            padding: 3rem 1rem;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 1.5rem;
          }

          .stat-item h3 {
            font-size: 2rem;
          }

          .stat-item p {
            font-size: 1rem;
          }

          .cta-section {
            padding: 3rem 1rem;
          }

          .cta-section h2 {
            font-size: 2rem;
          }
        }

        @media (max-width: 480px) {
          .user-welcome {
            font-size: 0.75rem;
            padding: 0.4rem 0.8rem;
          }

          .features {
            padding: 2rem 0.5rem;
          }

          .feature-card {
            padding: 1.5rem;
          }

          .section-title {
            font-size: 1.8rem;
          }

          .stats {
            padding: 2rem 0.5rem;
          }

          .stats-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }

          .cta-section {
            padding: 2rem 0.5rem;
          }

          .cta-section h2 {
            font-size: 1.8rem;
          }
        }

        /* Touch improvements */
        @media (hover: none) {
          .feature-card:hover {
            transform: none;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          }

          .cta-button:hover {
            transform: none;
          }

          .secondary-button:hover {
            transform: none;
          }
        }

        .hero-logo {
          width: 75%;
          max-width: 600px;
          height: auto;
          display: block;
          margin: 0 auto 2rem auto;
          animation: slideInUp 1s ease-out;
        }
      `}</style>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <img
            src={flowFrameLogo}
            alt="FlowFrame Logo"
            className="hero-logo"
            width="192"
            height="96"
            loading="eager"
          />
          <p className="tagline">Empowering Confident Artistry</p>
          <p className="description">
            The comprehensive suite of music practice tools designed to help
            musicians of all levels improve their skills, track their progress,
            and build confidence in their artistry.
          </p>
          <div className="hero-buttons">
            <button
              className="cta-button pulse"
              onClick={isLoggedIn ? onNavigateToTracker : showLoginModal}
            >
              {isLoggedIn ? "Continue Practicing" : "Start Practicing"}
            </button>
            <button
              className="secondary-button"
              onClick={() => smoothScroll("features")}
            >
              Explore Tools
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features" id="features">
        <div className="container">
          <h2
            className={`section-title fade-in ${
              isVisible.title ? "visible" : ""
            }`}
            data-fade-id="title"
          >
            Complete Practice Ecosystem
          </h2>
          <p
            className={`section-subtitle fade-in ${
              isVisible.subtitle ? "visible" : ""
            }`}
            data-fade-id="subtitle"
          >
            Four powerful tools working together to transform your musical
            journey
          </p>

          <div className="features-grid">
            <div
              className={`feature-card fade-in ${
                isVisible.metronome ? "visible" : ""
              }`}
              data-fade-id="metronome"
              onClick={onNavigateToMetronome}
            >
              <div className="feature-icon">
                <img 
                  src={flowFrameIcon} 
                  alt="FlowFrame Icon" 
                  style={{
                    width: '40px',
                    height: '40px',
                    objectFit: 'contain'
                  }}
                  width="40"
                  height="40"
                  loading="eager"
                />
              </div>
              <h3>FlowFrame Metronome</h3>
              <p>
                Advanced metronome with custom patterns, polyrhythms, and
                intelligent tempo tracking. Practice with precision and develop
                your internal timing.
              </p>
              <span className="feature-link">Try the Metronome →</span>
            </div>

            <div
              className={`feature-card fade-in ${
                isVisible.tracker ? "visible" : ""
              }`}
              data-fade-id="tracker"
              onClick={onNavigateToTracker}
            >
              <div className="feature-icon">📊</div>
              <h3>Journal</h3>
              <p>
                Track, time, and optimize your practice sessions. Create
                structured practice plans, monitor progress, and build
                consistent practice habits.
              </p>
              <span className="feature-link">Start Tracking →</span>
            </div>

            <div
              className={`feature-card fade-in ${
                isVisible.library ? "visible" : ""
              }`}
              data-fade-id="library"
              onClick={onNavigateToLibrary}
            >
              <div className="feature-icon">📚</div>
              <h3>Music Library</h3>
              <p>
                Discover and explore practice patterns created and shared by
                musicians worldwide. Find new rhythms, exercises, and techniques to enhance your practice.
              </p>
              <span className="feature-link">Browse Library →</span>
            </div>

            <div
              className={`feature-card fade-in ${
                isVisible.community ? "visible" : ""
              }`}
              data-fade-id="community"
              onClick={onNavigateToCommunity}
            >
              <div className="feature-icon">👥</div>
              <h3>Community Hub</h3>
              <p>
                Connect with fellow musicians, share your progress, and learn
                from others. Join a supportive community of dedicated artists.
              </p>
              <span className="feature-link">Join Community →</span>
            </div>

            <div
              className={`feature-card fade-in ${
                isVisible.scores ? "visible" : ""
              }`}
              data-fade-id="scores"
              onClick={onNavigateToScores}
            >
              <div className="feature-icon">🎼</div>
              <h3>Score Reader</h3>
              <p>
                Upload and view your sheet music PDFs with a full-screen
                performance mode, page-turn gestures, and seamless integration
                with your practice sessions.
              </p>
              <span className="feature-link">View Scores →</span>
            </div>

            <div
              className={`feature-card fade-in ${
                isVisible.about ? "visible" : ""
              }`}
              data-fade-id="about"
              onClick={onNavigateToAbout}
            >
              <div className="feature-icon">ℹ️</div>
              <h3>About FlowFrame</h3>
              <p>
                Learn about FlowFrame's comprehensive features, discover the story behind its creation by professional trombonist Michael Clayville, and get detailed instructions on how to maximize your practice experience.
              </p>
              <span className="feature-link">Learn More →</span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats" id="stats" ref={statsRef}>
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item">
              <h3 data-counter={platformStats.musiciansEmpowered}>
                {platformStats.loading ? (
                  <span style={{opacity: 0.6}}>Loading...</span>
                ) : (
                  platformStats.musiciansEmpowered > 0 ? '0' : 'N/A'
                )}
              </h3>
              <p>Musicians Empowered</p>
              {platformStats.isFailback && (
                <small style={{fontSize: '0.7rem', opacity: 0.6}}>*Estimated</small>
              )}
            </div>
            <div className="stat-item">
              <h3 data-counter={platformStats.practiceHoursTracked}>
                {platformStats.loading ? (
                  <span style={{opacity: 0.6}}>Loading...</span>
                ) : (
                  platformStats.practiceHoursTracked > 0 ? '0' : 'N/A'
                )}
              </h3>
              <p>Practice Hours Tracked</p>
              {platformStats.isFailback && (
                <small style={{fontSize: '0.7rem', opacity: 0.6}}>*Estimated</small>
              )}
            </div>
            <div className="stat-item">
              <h3 data-counter={platformStats.customPatternsCreated}>
                {platformStats.loading ? (
                  <span style={{opacity: 0.6}}>Loading...</span>
                ) : (
                  platformStats.customPatternsCreated > 0 ? '0' : 'N/A'
                )}
              </h3>
              <p>Custom Patterns Created</p>
              {platformStats.isFailback && (
                <small style={{fontSize: '0.7rem', opacity: 0.6}}>*Estimated</small>
              )}
            </div>
          </div>
          {platformStats.error && (
            <div className="stats-error">
              <p style={{fontSize: '0.8rem', opacity: 0.7, marginTop: '1rem', textAlign: 'center', color: '#ffeb3b'}}>
                ⚠️ {platformStats.isFailback ? 'Showing estimated values' : platformStats.error}
              </p>
            </div>
          )}
          {platformStats.loading && (
            <div className="stats-loading">
              <p style={{fontSize: '0.8rem', opacity: 0.7, marginTop: '1rem', textAlign: 'center'}}>
                📊 Loading latest statistics...
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div
            className={`cta-content fade-in ${isVisible.cta ? "visible" : ""}`}
            data-fade-id="cta"
          >
            <h2>Ready to Transform Your Practice?</h2>
            <p>
              Join the musicians who have already discovered the power
              of structured, intelligent practice with FlowFrame.
            </p>
            <button
              className="cta-button"
              onClick={isLoggedIn ? onNavigateToTracker : onGetStarted}
            >
              {isLoggedIn ? "Go to Journal" : "Start Your Journey"}
            </button>
          </div>
        </div>
      </section>

      <PWAPromptButton />

      {/* Footer */}
      <footer style={{
        textAlign: 'center',
        padding: '1.5rem 1rem',
        background: '#f1f5f9',
        borderTop: '1px solid #e2e8f0',
        fontFamily: '"Inter", "Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        fontSize: '0.85rem',
        color: '#94a3b8',
      }}>
        <a
          href="/privacy.html"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#64748b', textDecoration: 'none', borderBottom: '1px solid transparent', transition: 'border-color 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.borderBottomColor = '#64748b'}
          onMouseLeave={e => e.currentTarget.style.borderBottomColor = 'transparent'}
        >
          Privacy Policy
        </a>
        <span style={{ margin: '0 0.75rem' }}>·</span>
        <span>&copy; {new Date().getFullYear()} FlowFrame</span>
      </footer>
    </div>
  );
};

export default LandingPage;
